/**
 * Responsibility: Shape admin feature flags into field-assist form metadata and values.
 * Scope: Admin form metadata/value normalization only; persistence stays elsewhere.
 */
import type { FormMetaData } from 'field-assist'
import type { AdminFeatureFlags, AdminProviderOverride } from '../../admin/admin-contract'

const PROVIDER_OPTIONS: Array<{ text: string; value: AdminProviderOverride }> = [
  { text: 'Automatic', value: 'auto' },
  { text: 'Mock', value: 'mock' },
  { text: 'Ollama', value: 'ollama' },
  { text: 'Vertex Managed', value: 'vertex-managed' },
  { text: 'Groq', value: 'groq' },
  { text: 'OpenAI', value: 'openai' },
  { text: 'Gemini', value: 'gemini' },
  { text: 'Anthropic', value: 'anthropic' },
]

const MANAGED_ACCESS_OPTIONS: Array<{ text: string; value: string }> = [
  { text: 'Open (Public)', value: 'open' },
  { text: 'Entitlements (Plan-based)', value: 'entitlements' },
  { text: 'Grants Only (Admin-curated)', value: 'grants_only' },
]

export function buildAdminFlagInitialValues(flags: AdminFeatureFlags): Record<string, string | number | boolean> {
  return {
    maintenanceMode: flags.maintenanceMode === true,
    registrationOpen: flags.registrationOpen !== false,
    providerOverride: flags.providerOverride ?? 'auto',
    managedAccessMode: flags.managedAccessMode ?? 'entitlements',
    logicQuotaPerHour: Number(flags.logicQuotaPerHour ?? 0),
    imageQuotaPerDay: Number(flags.imageQuotaPerDay ?? 0),
  }
}

export function buildAdminFlagsFormMeta(): FormMetaData {
  return {
    maintenanceMode: {
      label: 'Maintenance Mode',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
    registrationOpen: {
      label: 'Guest Registration Open',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
    providerOverride: {
      label: 'AI Provider Override',
      componentType: 'select',
      options: PROVIDER_OPTIONS,
      attributes: {
        class: 'admin-flag-select',
      },
    },
    managedAccessMode: {
      label: 'Managed Cloud Access',
      componentType: 'select',
      options: MANAGED_ACCESS_OPTIONS,
      attributes: {
        class: 'admin-flag-select',
      },
    },
    logicQuotaPerHour: {
      label: 'Logic Calls Per Hour',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    imageQuotaPerDay: {
      label: 'Image Calls Per Day',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
  }
}

function readBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return /^(true|1|on|yes)$/i.test(value)
  return fallback
}

function readInt(value: unknown): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : 0
}

export function buildAdminFlagSubmission(values: Record<string, unknown>, current: AdminFeatureFlags): AdminFeatureFlags {
  const providerValue = String(values.providerOverride ?? current.providerOverride ?? 'auto') as AdminProviderOverride
  const providerOverride = PROVIDER_OPTIONS.some(option => option.value === providerValue)
    ? providerValue
    : 'auto'
  const accessValue = String(values.managedAccessMode ?? current.managedAccessMode ?? 'entitlements')
  const managedAccessMode = MANAGED_ACCESS_OPTIONS.some(option => option.value === accessValue)
    ? accessValue as any
    : 'entitlements'

  return {
    maintenanceMode: readBool(values.maintenanceMode, current.maintenanceMode === true),
    registrationOpen: readBool(values.registrationOpen, current.registrationOpen !== false),
    providerOverride,
    managedAccessMode,
    logicQuotaPerHour: readInt(values.logicQuotaPerHour),
    imageQuotaPerDay: readInt(values.imageQuotaPerDay),
    updatedAtMs: current.updatedAtMs,
  }
}
